import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, withQuery } from './http'
import { qk } from './queryKeys'
import type { EmergencyAlert } from './types'

/** How often the alert banner re-checks while the live connection is down. An SOS shouldn't wait on a slow poll. */
export const ALERT_POLL_MS = 15_000
/** With the live connection up (see emergencyRealtime.ts) polling is only a safety net for a missed event. */
export const ALERT_LIVE_FALLBACK_POLL_MS = 60_000
export const SITE_ALERTS_POLL_MS = 30_000

export const emergencyApi = {
  /** Every active alert at the sites the caller can see. */
  active: (signal?: AbortSignal) => apiFetch<EmergencyAlert[]>(withQuery('/emergencies', { active: true }), { signal }),
  /** One site's alerts, newest first, active and resolved. */
  forSite: (siteId: string, signal?: AbortSignal) => apiFetch<EmergencyAlert[]>(`/sites/${siteId}/emergencies`, { signal }),
  resolve: (siteId: string, alertId: string) => apiFetch<EmergencyAlert>(`/sites/${siteId}/emergencies/${alertId}/resolve`, { method: 'POST' }),
}

export const emergencyQueries = {
  /** `live` says the hub connection is up, which relaxes the poll. Polls in a background tab too, so the tab title
   * still announces an alert to someone working in another window. */
  active: (live = false) =>
    queryOptions({
      queryKey: qk.emergencies.active,
      queryFn: ({ signal }) => emergencyApi.active(signal),
      refetchInterval: live ? ALERT_LIVE_FALLBACK_POLL_MS : ALERT_POLL_MS,
      refetchIntervalInBackground: true,
      staleTime: 5_000,
    }),
  forSite: (siteId: string) =>
    queryOptions({
      queryKey: qk.emergencies.site(siteId),
      queryFn: ({ signal }) => emergencyApi.forSite(siteId, signal),
      refetchInterval: SITE_ALERTS_POLL_MS,
      staleTime: 5_000,
    }),
}

/** Refreshes every alert list afterwards, win or lose: a 409 means someone else resolved it first, and the lists
 * should show that. Doesn't wait for the refresh, so the caller hears the outcome straight away. */
export function useResolveEmergency() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ siteId, alertId }: { siteId: string; alertId: string }) => emergencyApi.resolve(siteId, alertId),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.emergencies.all }),
  })
}
